/**
 * What each venue role can do, in the owner's own words, tied to the rows that actually grant it.
 *
 * The danger with role copy is that it drifts: someone adds a permission row, the cloud starts admitting
 * managers to something new, and the sentence under "Manager" keeps describing last month's system. So
 * the sentences are not free text sitting beside the table — every capability an operator reads here is
 * mapped to the permission row that grants it, and `roleCopy.test.ts` fails when a row appears, moves
 * tier, or loses its words. A table change breaks the test rather than quietly making the screen lie.
 *
 * The copy itself is the owner's (2026-09-14). His words ship verbatim, including the two nouns the copy
 * law would otherwise refuse — see `operatorCopy.allow.ts` for why that exception is his to make.
 */

import { PERMISSIONS, type PermissionName, type PermissionTier } from './permissions.generated';

/** The three roles a venue can assign, weakest first. Staff and support are not assignable. */
export const ASSIGNABLE_ROLES = ['operator', 'manager', 'owner'] as const;
export type AssignableRoleName = typeof ASSIGNABLE_ROLES[number];

/**
 * The operator-facing words for every permission row, or `null` where the row has no words in the
 * sentence because it is either covered there ("runs the room" includes `room.view`) or has its
 * own label in `PERMISSION_ROW_LABELS`.
 *
 * Every row in the table must appear here. A new row with no decision fails the test, which is the
 * point: someone has to say whether it changes what a person is told.
 */
export const CAPABILITY_WORDS: Record<PermissionName, string | null> = {
  // Operator — running the room.
  'scenes.manage': 'scenes',
  'scores.play': 'scores',
  'effects.run': 'effects',
  'notices.acknowledge': null,
  'records.write': null,
  'rig.override': null,
  'room.blackout': 'blackout',
  'room.view': null,
  'room.takeover': null,
  'stream.disconnect': null,

  // Manager — setting the room up.
  'network.manage': 'Wi-Fi',
  'rig.manage': 'rig',
  'updates.manage': 'updates',
  'room.intensity_limit': 'intensity limit',
  'scores.assign': 'scores',
  'scores.publish': 'scores',
  'scores.delete': 'scores',
  'members.manage': 'manage operators and managers',
  'effects.packages.manage': null,
  'pairing.manage': null,
  'remotelive.policy': null,
  'remotelive.session': null,
  'room.manage': null,
  'room.output_rate': null,

  // Owner — the venue itself.
  'device.manage': 'claim or remove the controller',
  'venue.transfer_ownership': 'change the owner',
  'venue.delete': null,

  // Nuance staff. Never shown in a venue's role picker.
  'effects.library.manage': null,
  'releases.read': null,
};

/** The tier each assignable role sits at, so a line can be checked against the rows it newly admits. */
export const ROLE_TIER: Record<AssignableRoleName, PermissionTier> = {
  operator: 'operator',
  manager: 'manager',
  owner: 'owner',
};

/** Owner-ratified labels for permission rows shown beneath their role's existing sentence. */
export const PERMISSION_ROW_LABELS = {
  'notices.acknowledge': 'Acknowledge notices',
  'records.write': 'Edit rig records',
  'rig.override': 'Override a rig',
} as const satisfies Partial<Record<PermissionName, string>>;

export function permissionRowsAddedByRole(role: AssignableRoleName): string[] {
  return (Object.keys(PERMISSION_ROW_LABELS) as Array<keyof typeof PERMISSION_ROW_LABELS>)
    .filter((permission) => PERMISSIONS[permission] === ROLE_TIER[role])
    .map((permission) => PERMISSION_ROW_LABELS[permission]);
}

/**
 * One sentence per role: what it can do that the role below it cannot.
 *
 * Owner's wording, 2026-09-14, shipped as written.
 */
export const ROLE_DESCRIPTIONS: Record<AssignableRoleName, string> = {
  operator: 'Runs the room (scenes, scores, effects, blackout).',
  manager: 'Everything an operator can, plus set up the room — Wi-Fi, rig, updates, intensity limit, scores, and manage operators and managers.',
  owner: 'Everything a manager can, plus claim or remove the controller and change the owner.',
};

/** Every permission row granted at exactly `tier` — the rows a role adds over the role beneath it. */
export function rowsAddedAtTier(tier: PermissionTier): PermissionName[] {
  return (Object.keys(PERMISSIONS) as PermissionName[])
    .filter((permission) => PERMISSIONS[permission] === tier)
    .sort();
}

/** The distinct words a role's own rows contribute, in the order the sentence should mention them. */
export function capabilitiesAddedByRole(role: AssignableRoleName): string[] {
  const seen = new Set<string>();
  for (const permission of rowsAddedAtTier(ROLE_TIER[role])) {
    const words = CAPABILITY_WORDS[permission];
    if (words) seen.add(words);
  }
  return [...seen];
}
